import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatBadgeModule } from '@angular/material/badge';
import { MatMenuModule } from '@angular/material/menu';
import { MatDividerModule } from '@angular/material/divider';
import { MatRippleModule } from '@angular/material/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog } from '@angular/material/dialog';
import { SwPush } from '@angular/service-worker';
import { Subscription } from 'rxjs';

import { UserNotificationService, InAppNotification } from 'src/app/services/user-notification.service';
import { NotificationService } from 'src/app/services/notification.service';
import { BackendService } from 'src/app/services/backend.service';
import { NotificationDetailDialogComponent } from 'src/app/views/notifications/detail/notification-detail-dialog.component';
import { ProcessHandlerService } from 'src/app/process-engine/process/process-handler-dialog/process-handler.service';

@Component({
  selector: 'app-notification-dropdown',
  templateUrl: './notification-dropdown.component.html',
  styleUrls: ['./notification-dropdown.component.scss'],
  imports: [
    CommonModule,
    RouterModule,
    TranslocoModule,
    MatIconModule,
    MatButtonModule,
    MatBadgeModule,
    MatMenuModule,
    MatDividerModule,
    MatRippleModule,
    MatTooltipModule
  ]
})
export class NotificationDropdownComponent implements OnInit, OnDestroy {
  pushSubscribed = false;
  pushEnabled = false;
  private pushSub: Subscription;

  constructor(
    public readonly notificationService: UserNotificationService,
    private readonly pushNotificationService: NotificationService,
    private readonly backendService: BackendService,
    private readonly swPush: SwPush,
    private readonly dialog: MatDialog,
    private readonly processHandlerService: ProcessHandlerService,
    private readonly transloco: TranslocoService
  ) {}

  get pushTooltipText(): string {
    const base = this.pushSubscribed
      ? this.transloco.translate('notifications.dropdown.push_disable')
      : this.transloco.translate('notifications.dropdown.push_enable');
    const n = this.notificationService.pushSubscriptionCount;
    const devicesKey =
      n === 1 ? 'notifications.dropdown.push_devices_count_one' : 'notifications.dropdown.push_devices_count_other';
    const devices = this.transloco.translate(devicesKey, { count: n });
    return `${base}\n${devices}`;
  }

  ngOnInit(): void {
    this.pushEnabled = this.swPush.isEnabled;
    if (this.pushEnabled) {
      this.pushSub = this.swPush.subscription.subscribe((sub) => {
        this.pushSubscribed = !!sub;
      });
    }
  }

  ngOnDestroy(): void {
    this.pushSub?.unsubscribe();
  }

  togglePush(event: MouseEvent): void {
    event.stopPropagation();

    if (this.pushSubscribed) {
      this.disablePush();
    } else {
      this.enablePush();
    }
  }

  private enablePush(): void {
    this.pushNotificationService.setupPush().subscribe({
      next: (sub) => {
        const json = sub.toJSON();
        this.backendService
          .post<'notification/pushSubscribe'>('notifications/push/subscribe', {
            body: {
              endpoint: json.endpoint!,
              keys: json.keys as { p256dh: string; auth: string }
            }
          })
          .subscribe({
            next: () => {
              this.pushNotificationService.success('Push-Benachrichtigungen aktiviert');
              this.notificationService.notifyNotificationsChanged();
            },
            error: () => this.pushNotificationService.error('Push-Benachrichtigungen konnten nicht aktiviert werden')
          });
      },
      error: () => {}
    });
  }

  private disablePush(): void {
    this.pushNotificationService.getSubscription().subscribe({
      next: (sub) => {
        if (!sub) return;
        const endpoint = sub.endpoint;
        sub.unsubscribe().then(() => {
          this.backendService
            .delete<'notification/pushUnsubscribe'>('notifications/push/subscribe', { query: { endpoint } })
            .subscribe({
              next: () => {
                this.pushNotificationService.success('Push-Benachrichtigungen deaktiviert');
                this.notificationService.notifyNotificationsChanged();
              },
              error: () => this.pushNotificationService.error('Push-Benachrichtigungen konnten nicht deaktiviert werden')
            });
        });
      }
    });
  }

  openDetail(notification: InAppNotification): void {
    const dimensions = this.processHandlerService.getDialogScreenSize();
    this.dialog.open(NotificationDetailDialogComponent, {
      ...dimensions,
      data: { notification }
    });
  }

  /**
   * Format a date string as relative time (e.g. "5 min ago")
   */
  getRelativeTime(dateString: string | undefined): string {
    if (!dateString) return '';

    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMinutes = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMinutes < 1) return 'Gerade eben';
    if (diffMinutes < 60) return `${diffMinutes} Min.`;
    if (diffHours < 24) return `${diffHours} Std.`;
    if (diffDays < 7) return `${diffDays} T.`;
    return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  }

  isUnread(notification: InAppNotification): boolean {
    return notification.status !== 'read' && notification.status !== 'dismissed' && notification.status !== 'archived';
  }
}
